/**
 * EXPAI-002 — Sentry EU integration (frontend).
 *
 * GDPR notes:
 *   - We send only the user **ID** (no email, name, or IP) — `sendDefaultPii: false`.
 *   - DSN must point to an EU region ingest host (`*.ingest.de.sentry.io`)
 *     so events stay inside the EU.
 *   - If `VITE_SENTRY_DSN` is missing the module is a complete no-op
 *     (safe in local/dev/preview without a DSN).
 */
import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV = (import.meta.env.VITE_SENTRY_ENV as string | undefined)
  ?? import.meta.env.MODE
  ?? 'development';
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

let initialized = false;

/**
 * Validate Sentry-related env vars before touching `Sentry.init`.
 * Returns the validated DSN, or `null` when init must be skipped.
 *
 * Guards against:
 *   - Missing / empty DSN.
 *   - Placeholder values committed in `.env.example`
 *     (`your-dsn`, `changeme`, `xxxx`, etc.).
 *   - Malformed DSN URLs (Sentry SDK throws synchronously otherwise,
 *     which would crash app bootstrap before React mounts).
 */
function validateSentryEnv(): string | null {
  if (!DSN) return null;

  const trimmed = DSN.trim();
  if (!trimmed) return null;

  if (/^(your-|changeme|placeholder|xxx+|todo)/i.test(trimmed)) {
    // eslint-disable-next-line no-console
    console.warn('[Sentry] VITE_SENTRY_DSN looks like a placeholder — skipping init.');
    return null;
  }

  try {
    const url = new URL(trimmed);
    if (!/^https?:$/.test(url.protocol) || !url.hostname.includes('sentry')) {
      // eslint-disable-next-line no-console
      console.warn('[Sentry] VITE_SENTRY_DSN is not a valid Sentry DSN — skipping init.');
      return null;
    }
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[Sentry] VITE_SENTRY_DSN is not a parseable URL — skipping init.');
    return null;
  }

  // Soft warning if DSN does not look EU-hosted.
  if (!/\.ingest\.de\.sentry\.io/.test(trimmed) && !/\.de\.sentry\.io/.test(trimmed)) {
    // eslint-disable-next-line no-console
    console.warn('[Sentry] DSN does not look like an EU region host — verify GDPR compliance.');
  }

  return trimmed;
}

export function initSentry(): void {
  if (initialized) return;

  const validDsn = validateSentryEnv();
  if (!validDsn) return;

  Sentry.init({
    dsn: validDsn,
    environment: ENV,
    release: RELEASE,
    sendDefaultPii: false, // GDPR: no IP, no cookies, no headers
    tracesSampleRate: ENV === 'production' ? 0.1 : 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    beforeSend(event) {
      // Strip anything that could leak PII even if a library injected it.
      if (event.user) {
        event.user = { id: event.user.id };
      }
      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
      }
      return event;
    },
  });

  initialized = true;
}

export function isSentryEnabled(): boolean {
  return initialized;
}

/** Identify the current user — only the ID is sent (GDPR). */
export function setSentryUser(userId: string | null | undefined): void {
  if (!initialized) return;
  if (!userId) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: userId });
}

/** Clear the current user (call on logout). */
export function clearSentryUser(): void {
  if (!initialized) return;
  Sentry.setUser(null);
}

/**
 * Capture an exception with optional structured context.
 * No-op when Sentry is disabled — safe to call from anywhere.
 */
export function captureException(
  error: unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
    fingerprint?: string[];
  }
): void {
  if (!initialized) {
    // eslint-disable-next-line no-console
    console.error('[captureException]', error, context);
    return;
  }
  Sentry.withScope((scope) => {
    if (context?.tags) {
      for (const [k, v] of Object.entries(context.tags)) scope.setTag(k, v);
    }
    if (context?.extra) {
      for (const [k, v] of Object.entries(context.extra)) scope.setExtra(k, v);
    }
    if (context?.level) scope.setLevel(context.level);
    if (context?.fingerprint) scope.setFingerprint(context.fingerprint);
    Sentry.captureException(error);
  });
}

/** Add a breadcrumb (no-op if Sentry disabled). */
export function addBreadcrumb(breadcrumb: {
  category?: string;
  message: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, unknown>;
}): void {
  if (!initialized) return;
  Sentry.addBreadcrumb({
    category: breadcrumb.category ?? 'app',
    message: breadcrumb.message,
    level: breadcrumb.level ?? 'info',
    data: breadcrumb.data,
    timestamp: Date.now() / 1000,
  });
}

// ─── Aliases requested by EXPAI-002 ───────────────────────────────────────────
export const captureError = captureException;
export const setUserContext = setSentryUser;
export const clearUserContext = clearSentryUser;

export { Sentry };
